<template>
  <div class="page page-private-lib">
    <div class="lib-header">
      <div class="lib-back" @click="goBack">
        <i class="el-icon-arrow-left" />
        <span>返回</span>
      </div>
      <h2 class="lib-title">私有库</h2>
    </div>

    <div class="lib-tabs">
      <div
        v-for="tab in tabs"
        :key="tab.type"
        :class="['tab-item', { active: activeTab === tab.type }]"
        @click="switchTab(tab.type)"
      >
        {{ tab.label }}
      </div>
    </div>

    <div class="lib-add" @click="openAdd">
      <span>+ 新增{{ currentTabLabel }}</span>
    </div>

    <div v-loading="loading" class="lib-grid">
      <div v-for="item in items" :key="item.id" class="lib-card">
        <div class="card-title">{{ item.title }}</div>

        <!-- 桥段配置 / 写作风格: 纯文本 -->
        <div v-if="isTextType" class="card-desc">{{ item.content }}</div>

        <!-- 特色词汇: 灰色标签 -->
        <div v-if="activeTab === 3" class="card-tags">
          <span
            v-for="(word, wi) in parseWords(item.content)"
            :key="wi"
            class="tag tag-gray"
          >{{ word }}</span>
        </div>

        <!-- 热梗奇思: 橙色标签 -->
        <div v-if="activeTab === 4" class="card-tags">
          <span
            v-for="(word, wi) in parseWords(item.content)"
            :key="wi"
            class="tag tag-orange"
          >{{ word }}</span>
        </div>

        <div class="card-actions">
          <button class="btn-edit" @click="openEdit(item)">编辑</button>
          <button class="btn-delete" @click="handleDelete(item)">删除</button>
        </div>
      </div>

      <div v-if="!loading && items.length === 0" class="empty-tip">
        暂无数据，点击上方"+ 新增{{ currentTabLabel }}"添加
      </div>
    </div>

    <!-- 新增/编辑弹窗：桥段配置 & 写作风格 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="80px" size="small">
        <el-form-item label="名称">
          <el-input v-model="form.title" placeholder="请输入名称" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item v-if="isTextType" label="描述">
          <el-input
            type="textarea"
            :rows="4"
            v-model="form.content"
            placeholder="请输入描述内容"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
        <el-form-item v-if="isTagType" label="词条">
          <div class="tag-editor">
            <div class="tag-list">
              <el-tag
                v-for="(word, idx) in formWords"
                :key="idx"
                closable
                size="medium"
                :type="activeTab === 4 ? 'warning' : 'info'"
                @close="removeWord(idx)"
              >{{ word }}</el-tag>
            </div>
            <div class="tag-input-row">
              <el-input
                v-model="newWord"
                size="small"
                placeholder="输入词条后按回车添加"
                @keyup.enter.native="addWord"
              />
              <el-button size="small" type="primary" @click="addWord">添加</el-button>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  listPrivateLib,
  createPrivateLib,
  updatePrivateLib,
  deletePrivateLib
} from '@/api/privateLib';

export default {
  name: 'PrivateLib',
  data() {
    return {
      tabs: [
        { type: 1, label: '桥段配置' },
        { type: 2, label: '写作风格' },
        { type: 3, label: '特色词汇' },
        { type: 4, label: '热梗奇思' }
      ],
      activeTab: 1,
      items: [],
      loading: false,
      dialogVisible: false,
      saving: false,
      editingId: null,
      form: { title: '', content: '' },
      formWords: [],
      newWord: ''
    };
  },
  computed: {
    currentTabLabel() {
      const t = this.tabs.find(t => t.type === this.activeTab);
      return t ? t.label : '';
    },
    dialogTitle() {
      return (this.editingId ? '编辑' : '新增') + this.currentTabLabel;
    },
    isTextType() {
      return this.activeTab === 1 || this.activeTab === 2;
    },
    isTagType() {
      return this.activeTab === 3 || this.activeTab === 4;
    }
  },
  created() {
    this.loadData();
  },
  methods: {
    goBack() {
      this.$router.push('/projects');
    },
    switchTab(type) {
      if (this.activeTab === type) return;
      this.activeTab = type;
      this.loadData();
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await listPrivateLib(this.activeTab);
        this.items = res.data || [];
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    parseWords(content) {
      if (!content) return [];
      try {
        const arr = JSON.parse(content);
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    },
    openAdd() {
      this.editingId = null;
      this.form = { title: '', content: '' };
      this.formWords = [];
      this.newWord = '';
      this.dialogVisible = true;
    },
    openEdit(item) {
      this.editingId = item.id;
      this.form = { title: item.title, content: item.content || '' };
      if (this.isTagType) {
        this.formWords = this.parseWords(item.content);
      }
      this.newWord = '';
      this.dialogVisible = true;
    },
    addWord() {
      const w = this.newWord.trim();
      if (!w) return;
      if (this.formWords.includes(w)) {
        this.$message.warning('该词条已存在');
        return;
      }
      this.formWords.push(w);
      this.newWord = '';
    },
    removeWord(idx) {
      this.formWords.splice(idx, 1);
    },
    async submitForm() {
      if (!this.form.title.trim()) {
        this.$message.warning('请输入名称');
        return;
      }
      if (this.isTextType && !this.form.content.trim()) {
        this.$message.warning('请输入描述内容');
        return;
      }
      if (this.isTagType && this.formWords.length === 0) {
        this.$message.warning('请至少添加一个词条');
        return;
      }

      const payload = {
        type: this.activeTab,
        title: this.form.title.trim(),
        content: this.isTagType
          ? JSON.stringify(this.formWords)
          : this.form.content.trim()
      };

      this.saving = true;
      try {
        if (this.editingId) {
          await updatePrivateLib(this.editingId, payload);
          this.$message.success('保存成功');
        } else {
          await createPrivateLib(payload);
          this.$message.success('新增成功');
        }
        this.dialogVisible = false;
        this.loadData();
      } catch (e) {
        // request 拦截器已处理
      } finally {
        this.saving = false;
      }
    },
    handleDelete(item) {
      this.$confirm(`确定要删除「${item.title}」吗？`, '删除确认', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      }).then(async () => {
        try {
          await deletePrivateLib(item.id);
          this.$message.success('删除成功');
          this.loadData();
        } catch (e) {
          // request 拦截器已处理
        }
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.page-private-lib {
  height: 100%;
  background: #f5f7fa;
  overflow-y: auto;
  padding: 32px 48px;
}

/* 头部 */
.lib-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}
.lib-back {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  transition: color 0.2s;
}
.lib-back:hover {
  color: #ff7f50;
}
.lib-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

/* 标签栏 */
.lib-tabs {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}
.tab-item {
  padding: 10px 36px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  background: #fff;
  border: 1px solid #e4e7ed;
  cursor: pointer;
  transition: all 0.25s;
  user-select: none;
}
.tab-item:hover {
  border-color: #ff9472;
  color: #ff7f50;
}
.tab-item.active {
  background: linear-gradient(135deg, #ff9472 0%, #ff7f50 100%);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(255, 127, 80, 0.3);
}

/* 新增按钮 */
.lib-add {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border: 1px dashed #dcdfe6;
  border-radius: 12px;
  background: #fff;
  color: #909399;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 24px;
}
.lib-add:hover {
  border-color: #ff9472;
  color: #ff7f50;
  background: #fff8f5;
}

/* 卡片网格 */
.lib-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  min-height: 120px;
}
@media (max-width: 960px) {
  .lib-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 640px) {
  .lib-grid {
    grid-template-columns: 1fr;
  }
}

.lib-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #ebeef5;
  transition: box-shadow 0.2s, border-color 0.2s;
}
.lib-card:hover {
  border-color: #f0d0c0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}
.card-desc {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 标签区域 */
.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 14px;
  font-size: 12px;
  line-height: 1.5;
}
.tag-gray {
  background: #f4f4f5;
  color: #606266;
  border: 1px solid #e9e9eb;
}
.tag-orange {
  background: #fff3ed;
  color: #e86a33;
  border: 1px solid #ffdbc9;
}

/* 操作按钮 */
.card-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f5f7fa;
}
.btn-edit,
.btn-delete {
  padding: 6px 24px;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #dcdfe6;
  transition: all 0.2s;
}
.btn-edit {
  background: #f9f9fa;
  color: #606266;
}
.btn-edit:hover {
  border-color: #c0c4cc;
  background: #f0f0f2;
}
.btn-delete {
  background: #fff;
  color: #f56c6c;
  border-color: #fde2e2;
}
.btn-delete:hover {
  background: #fef0f0;
  border-color: #f56c6c;
}

.empty-tip {
  grid-column: 1 / -1;
  text-align: center;
  color: #c0c4cc;
  font-size: 14px;
  padding: 40px 0;
}

/* 弹窗内标签编辑器 */
.tag-editor .tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  min-height: 32px;
}
.tag-editor .tag-input-row {
  display: flex;
  gap: 8px;
}
.tag-editor .tag-input-row .el-input {
  flex: 1;
}
</style>
